"use client"

import { useRef, useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import axios from "axios"
import { setUser } from "../Redux/userSlice"
import { apiService } from "../services/apiService"
import { Toast } from "primereact/toast" // Import Toast component
import Layout from "../Layout/Layout"
import "./Landing.css"

const Landing = () => {
  const toast = useRef(null)
  const inputRef = useRef(null)
  const [hasAnalyzed, setHasAnalyzed] = useState(false)
  const [totalSize, setTotalSize] = useState(0)
  const [selectedFile, setSelectedFile] = useState(null)
  const [dragActive, setDragActive] = useState(false)
  const [selectedFileName, setSelectedFileName] = useState("")
  const [recentAnalyses, setRecentAnalyses] = useState([])
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { name, email, freeScanUsed, credits } = useSelector((state) => state.user)

  useEffect(() => {
    const fetchRecent = async () => {
      try {
        const res = await apiService.getRecentAnalyses(email)
        console.log("Recent analyses:", res)
        setRecentAnalyses(res.body || [])
      } catch (err) {
        console.error("Failed to fetch recent analyses", err)
        showToast("error", "Error", "Failed to fetch recent analyses")
      }
    }
    if (email) fetchRecent()
  }, [email])

  useEffect(() => {
    const fetchCredits = async () => {
      if (email) {
        try {
          const response = await apiService.getCredits(email)
          dispatch(setUser({ credits: response.body.credits }))
        } catch (error) {
          console.error("Failed to fetch credits:", error)
          showToast("error", "Error", "Failed to fetch credits. Please try again later.")
        }
      }
    }
    fetchCredits()
  }, [email, dispatch])

  const showToast = (severity, summary, detail) => {
    toast.current.show({
      severity: severity,
      summary: summary,
      detail: detail,
      life: 3000, // Toast duration in milliseconds
    })
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (!file) return

    setHasAnalyzed(false)
    if (file.size > 5 * 1024 * 1024) {
      showToast("error", "File too large", "Max size is 5MB")
      return
    }

    if (freeScanUsed && credits <= 0) {
      showToast("warn", "No Free Scan or Credits", "You need to buy credits to continue.")
      return
    }

    setSelectedFile(file)
    setSelectedFileName(file.name)
    setTotalSize(file.size)
    showToast("success", "File Selected", file.name)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragActive(false)
    const file = e.dataTransfer.files[0]
    if (file) {
      handleFileChange({ target: { files: [file] } })
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setDragActive(true)
  }

  const handleDragLeave = () => {
    setDragActive(false)
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
    setSelectedFileName("")
    setTotalSize(0)
    if (inputRef.current) inputRef.current.value = ""
    setHasAnalyzed(false)
    showToast("info", "File Removed", "You can upload another file now.")
  }

  const handleContinueAnalysis = async () => {
    try {
      setLoading(true)
      const file = selectedFile
      if (!file) {
        showToast("warn", "No File", "Please select a file")
        return
      }

      if (freeScanUsed && credits <= 0) {
        showToast("warn", "Insufficient Credits", "Please buy credits to proceed with analysis.")
        return
      }

      console.log("Selected file:", { name: file.name, type: file.type, size: file.size })

      const presignRes = await axios.post(
        "https://jdzlvzjz97.execute-api.us-east-2.amazonaws.com/v1/CRA/generate_presigned_url",
        { name, email, contentType: file.type },
      )

      const { uploadUrl, fileKey } = presignRes.data.body
      console.log("Pre-signed URL:", uploadUrl)
      console.log("File key:", fileKey)

      const uploadRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
        },
        body: file,
      })

      const text = await uploadRes.text()
      console.log("S3 Upload Response:", { status: uploadRes.status, text })
      if (!uploadRes.ok) {
        throw new Error(`S3 Upload failed: ${uploadRes.status} - ${text}`)
      }

      const documentURL = `https://cra2908.s3.us-east-2.amazonaws.com/${fileKey}`
      console.log("Document URL:", documentURL)

      const analyzeRes = await axios.post(
        "https://jdzlvzjz97.execute-api.us-east-2.amazonaws.com/v1/CRA/analyze-document",
        { documentURL, email, name },
      )

      showToast("success", "Analysis Started", "Redirecting to results...")
      setHasAnalyzed(true)
      console.log("Analysis response:", analyzeRes.data)
      navigate("/analysis", { state: { data: analyzeRes.data.body } })
    } catch (err) {
      console.error("Analysis error:", err)
      showToast("error", "Analysis failed", err.response?.data?.message || err.message)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status) => {
    return (
      <span className={`status-badge ${status === "Complete" ? "status-complete" : "status-error"}`}>
        <span>{status === "Complete" ? "✓" : "✕"}</span>
        {status}
      </span>
    )
  }

  const recentAnalysesFormatted = recentAnalyses
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 3)
    .map((item) => ({
      ...item,
      file: item.fileUrl.split("/").pop(),
      status: item.analysis.error || item.analysis.html?.includes("error") ? "Error" : "Complete",
      date: new Date(item.createdAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
    }))

  return (
    <Layout>
      <div className="landing-container">
        {/* Hero Section */}
        <div className="hero-section">
          <div className="hero-content">
            <h1 className="hero-title">Analyze Your Contracts with AI</h1>
            <p className="hero-subtitle">
              Get instant insights, risk assessments, and actionable recommendations for your legal documents using
              advanced artificial intelligence.
            </p>
            <div className="hero-features">
              <div className="hero-feature">
                <span className="hero-feature-icon">⚡</span>
                <span className="hero-feature-text">Instant Analysis</span>
              </div>
              <div className="hero-feature">
                <span className="hero-feature-icon">🎯</span>
                <span className="hero-feature-text">Risk Detection</span>
              </div>
              <div className="hero-feature">
                <span className="hero-feature-icon">📊</span>
                <span className="hero-feature-text">Detailed Reports</span>
              </div>
              <div className="hero-feature">
                <span className="hero-feature-icon">🔒</span>
                <span className="hero-feature-text">Secure & Private</span>
              </div>
            </div>
          </div>
        </div>

        {/* Upload Section */}
        <div className="upload-section">
          <h2 className="upload-title">Upload Your Contract</h2>
          <div
            className={`upload-zone ${dragActive ? "drag-active" : ""} ${selectedFile ? "file-selected" : ""}`}
            onClick={() => !selectedFile && inputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.docx"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />

            {!selectedFile ? (
              <>
                <div className="upload-icon">📄</div>
                <div className="upload-text">Click or drag your contract here</div>
                <div className="upload-subtext">Upload PDF or DOCX files up to 5MB</div>
                <div className="upload-formats">
                  <span className="format-badge">PDF</span>
                  <span className="format-badge">DOCX</span>
                </div>
              </>
            ) : (
              <div className="animate-fade-in">
                <div className="file-info">
                  <div className="file-icon">📄</div>
                  <div className="file-details">
                    <div className="file-name">{selectedFileName}</div>
                    <div className="file-size">{formatFileSize(totalSize)}</div>
                  </div>
                </div>
                <div className="file-actions">
                  {(!freeScanUsed || credits > 0) && (
                    <button
                      className="action-button action-primary"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleContinueAnalysis()
                      }}
                      disabled={loading}
                    >
                      <span>🚀</span>
                      {loading ? "Analyzing..." : "Start Analysis"}
                    </button>
                  )}
                  {freeScanUsed && credits <= 0 && (
                    <button
                      className="action-button action-warning animate-pulse"
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate("/pricing")
                      }}
                    >
                      <span>💳</span>
                      Buy Credits
                    </button>
                  )}
                  <button
                    className="action-button action-danger"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemoveFile()
                    }}
                  >
                    <span>🗑️</span>
                    Remove
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Features Section */}
        <div className="features-section">
          <h2 className="features-title">What You'll Get</h2>
          <p className="features-subtitle">
            Our AI-powered analysis provides comprehensive insights to help you understand your contracts better.
          </p>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">📋</div>
              <h3 className="feature-title">Executive Summary</h3>
              <p className="feature-description">
                Get a comprehensive overview of your contract's key terms, obligations, and important clauses in plain
                language.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🚩</div>
              <h3 className="feature-title">Risk Assessment</h3>
              <p className="feature-description">
                Identify potential risks, unfavorable terms, and clauses that could impact your business interests.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">💡</div>
              <h3 className="feature-title">Smart Recommendations</h3>
              <p className="feature-description">
                Receive actionable recommendations and suggestions to improve contract terms and protect your interests.
              </p>
            </div>
          </div>
        </div>

        {/* Recent Analyses */}
        {recentAnalysesFormatted.length > 0 && (
          <div className="recent-section">
            <h2 className="recent-title">Recent Contract Analyses</h2>
            <table className="recent-table">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Status</th>
                  <th>Analyzed</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {recentAnalysesFormatted.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <div className="file-cell">
                        <div className="file-type-icon">📄</div>
                        <span>{item.file}</span>
                      </div>
                    </td>
                    <td>{getStatusBadge(item.status)}</td>
                    <td>{item.date}</td>
                    <td>
                      <button className="view-button" onClick={() => {console.log(item);navigate("/analysis", { state: { data: item } })}}>
                        View Analysis
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {recentAnalysesFormatted.length === 0 && (
          <div className="recent-section">
            <div className="empty-state">
              <div className="empty-icon">📊</div>
              <div className="empty-text">No analyses yet</div>
              <div className="empty-subtext">Upload your first contract to get started</div>
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="cta-section">
          <h2 className="cta-title">Ready for Unlimited Analysis?</h2>
          <p className="cta-description">
            Upgrade to get unlimited contract reviews, advanced features, and priority support for your business needs.
          </p>
          <button className="cta-button" onClick={() => navigate("/pricing")}>
            <span>💎</span>
            View Pricing Plans
          </button>
        </div>
        <Toast ref={toast} /> {/* Add Toast component at the bottom of the layout */}
      </div>
    </Layout>
  )
}

export default Landing